import { useEffect, useRef } from 'react';
import { BehaviorSubject } from 'rxjs';
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { fetchUserInformation, fetchUserPreferencesApi } from '../Api/User/fetchUserInformation';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { setDataDogUser } from '../datadog';
import { datadogLogs } from '@datadog/browser-logs';
import { updateUsersApi } from '../Api/User/updateUsersApi';
import {
  Account,
  AuthenticatedAdmin,
  AuthenticatedAdvisor,
  AuthenticatedClient,
  AuthenticatedUser,
  User,
  WidgetId
} from '../types';
import { getSessionSubject, useSession } from '../Api/useSession';
import userCustomizationChannel from '../Api/sockets/userCustomization';
import { Permission } from './GeneralStore';
import { addError } from './Error';
import { setCanEditTags } from './tagEditPermission';
import { updateSettingsApi } from '../Api/User/updateUserSettingsApi';
import { HomeWidgetDefs } from '../components/Pages/HomePage/HomeWidgetDefs';
import { CasesWidgetDefs } from '../components/Pages/SupportCases/CasesWidgetDefs';
import { InventoryWidgetDefs } from '../components/Pages/Inventory/InventoryWidgetDefs';
import { DefaultInventoryGridColumnDefs } from '../components/Pages/Inventory/inventoryGridColumnDefs';
import { GroupedGridColDef } from '../components/Compounds/UpstackDataGrid/gridColumnTypes';
import { WorkflowBaseColumns } from '../components/Pages/Orders/WorkflowOrdersGridColumnDefs';
import { CommonRequestColumns } from '../components/Pages/Orders/ServiceRequestGridColumnDefs';
import { DefaultCaseGridColumnDefs } from '../components/Pages/SupportCases/SupportCaseGridColDefs';
import { getColumnsByProduct } from '../components/Compounds/UpstackDataGrid/inventoryUtils';
import { initializePendo } from '../pendo';
import { createSettingsApi } from '../Api/User/CreateUserSettingsApi';
import {
  isNoNewerThanStore,
  isOlderThanStore,
  isRemovalPending,
  markKeyAnswered,
  markKeyUnanswered,
  notePersistedRemovals,
  notePreferencesVersion,
  PREFERENCES_SETTLE_LIMIT_MS,
  PREFERENCES_STILL_SAVING,
  preferencesWritesSettledBy,
  preferencesWritesUnanswered,
  resetPreferencesWrites,
  trackPreferencesWrite,
  withContentKey,
  withUnansweredKeys
} from './preferencesWrites';
import {
  ALL_ACCOUNT_VIEW_ROLES,
  ADVISE_STAFF_ROLES,
  FEATURE_ROLES,
  ALL_ACCOUNT_WRITE_ROLES,
  INTERNAL_ROLES,
  Role,
  SKIP_ACCOUNT_ROLES,
  userInSkipRoles
} from './Role';
import { useSearchParams } from 'react-router-dom';
import { getTabs } from '../utils/helpers';
import { getDefaultProjectDetailsGridColumnsDefs } from '../components/Pages/Projects/projectGridColumnsDefs';
import { ProjectWidgetDefs } from '../components/Pages/Projects/ProjectsWidgetDefs';
import { DefaultPhoneNumberGridColumnDefs } from '../components/Pages/PhoneNumbers/PhoneNumberGridColumnDefs';
import { DefaultLineItemGridColumnDefs } from '../components/Pages/LineItems/LineItemGridColumnDefs';
import { DefaultTagGridColumnDefs } from '../components/Compounds/Tagging/TagsGridColumnDefs';
import { getThoughtspotToken as getThoughtspotTokenApi } from '../Api/User/thoughtspotTokenApi';
import { getAccountById } from '../Api/Admin/Accounts/getAccountById';
import {
  PriceBenchmarkTabNames,
  priceBenchmarkToMarketColumns,
  priceBenchmarkToNeedsReviewColumns
} from '../components/Pages/CostIntelligence/PriceBenchmarkGridColumnDefs';
import type { FeatureFlags } from '../Api/useFeatureFlags';
import type { WorkflowTab } from '../Api/Orders/ordersApi';

export interface OrderRequestPreferences {
  disabledProductFamilies: string[];
  disabledProducts: Record<string, string[]>;
  isAdvancedForm: boolean;
  displayProductFamilyName: boolean;
  displayLocationCode?: boolean;
  advancedNewLocationSettings?: boolean;
  quoteReviewApprovalWorkflow?: boolean;
}

export interface Preferences {
  id: string;
  index: number;
  enabled: boolean;
  value?: string;
}

export interface GridPreferences {
  muiConfig: GridInitialStatePro;
  disabledFields: string[];
  customView?: boolean;
  id?: string;
  // Written by the API: per column, when its visibility last changed.
  visibilityChangedAt?: Record<string, string>;
  // Sent with a save: the columns it showed or hid, for the API to stamp. Never stored.
  visibilitySwitched?: string[];
}

export interface WidgetPreferences {
  widgetId: WidgetId;
  headerName: string;
  index: number;
  hide: boolean;
  disabled: boolean;
}

export interface Widgets {
  cases: WidgetPreferences[];
  home: WidgetPreferences[];
  inventory: WidgetPreferences[];
  projects: WidgetPreferences[];
}

export type InventoryGridKeys = {
  [key in `inventory_grid_${string}`]?: GridPreferences;
};

type ProjectGridKeys = {
  [key in `project_grid_${string}`]?: GridPreferences;
};

type RelatedProjectAssetGridKeys = {
  [key in `related_project_assets_grid_${string}`]?: GridPreferences;
};

type PriceBenchmarkGridKeys = {
  [key in `price_benchmark_grid_${PriceBenchmarkTabNames}`]?: GridPreferences;
};

type AdviseGridKeys = {
  [key in `advise_${string}_grid`]?: GridPreferences;
};

/** A saved Orders view, or an Orders report from before views: the workflow tabs it shows and its report sends. */
export interface OrdersViewPreferences extends Partial<GridPreferences> {
  included_stages?: WorkflowTab[];
}

type OrdersViewKeys = {
  [key in `order_grid_${string}`]?: OrdersViewPreferences;
};

const InventoryGridVisibilityDefaults = [
  'details',
  'origin',
  'address',
  'productDisplay',
  'supplier.name',
  'status',
  'mRC',
  'term',
  'contractEndDate',
  'providerAccountNumber',
  'renewalLanguage',
  'customerLocationName',
  'customerLabelOther',
  'demarcationDevice',
  'deviceDetails',
  'tags'
];

export interface UserPreferencesContent
  extends
    InventoryGridKeys,
    ProjectGridKeys,
    RelatedProjectAssetGridKeys,
    PriceBenchmarkGridKeys,
    AdviseGridKeys,
    OrdersViewKeys {
  notifications: Preferences[];
  historic: Preferences[];
  order_grid: GridPreferences;
  request_grid: GridPreferences;
  user_grid: GridPreferences;
  team_grid: GridPreferences;
  account_grid: GridPreferences;
  case_grid: GridPreferences;
  inventory_grid_All: GridPreferences;
  widgets: Widgets;
  selectedAccounts: Account[];
  comments?: boolean;
  supplier_contact?: boolean;
  project_grid_Active: GridPreferences;
  // Clobber-immune base key: unlike the per-tab keys the grid auto-saves, this holds the account's
  // authoritative disabledFields durably.
  project_grid?: GridPreferences;
  project_details_grid: GridPreferences;
  // Related Inventory base key; its per-tab keys below are auto-saved by the grid, so this holds the
  // account's disabledFields durably.
  related_project_assets_grid?: GridPreferences;
  related_project_assets_grid_All?: GridPreferences;
  phone_number_grid: GridPreferences;
  line_item_grid?: GridPreferences;
  tag_grid: GridPreferences;
  quote_grid?: GridPreferences;
  order_request?: OrderRequestPreferences;
  price_benchmark_grid_to_market?: GridPreferences;
  price_benchmark_grid_needs_review?: GridPreferences;
  inventory_locations_grid?: GridPreferences;
}

export interface UserPreferences {
  content: UserPreferencesContent;
  id?: number;
  portal_user_id?: number;
  /** When the API last saved them, which keeps the store from moving back to an older copy. */
  updated_at?: string;
}

export interface UseSharedUserReturn {
  clear: () => void;
  accountScope: Account[] | undefined;
  fetchUser: () => Promise<void>;
  fetchUserPreferences: () => Promise<void>;
  updateUser: () => Promise<void>;
}
export const defaultUserData: User = {
  email: '',
  role: undefined,
  account_name: '',
  accounts: [],
  id: '',
  account_id: '',
  created_at: '',
  updated_at: '',
  current_sign_in_at: 'last login date',
  impersonated: false,
  authenticated: false,
  roles: [],
  permissions: [],
  first_name: '',
  last_name: ''
};

export const gridInitialState = (cols?: GroupedGridColDef[], visibilityDefaults?: string[]) => {
  const visibility: GridColumnVisibilityModel = {};
  if (cols)
    cols.forEach((col: GroupedGridColDef) => {
      visibility[col.field] = col.defaultHidden
        ? false
        : !visibilityDefaults || (visibilityDefaults?.includes(col.field) ?? false);
    });

  return {
    pinnedColumns: {},
    columns: {
      columnVisibilityModel: visibility,
      orderedFields: cols ? cols.map(({ field }) => field) : [],
      dimensions: {}
    },
    preferencePanel: {
      open: false
    },
    filter: {
      filterModel: {
        items: [],
        logicOperator: 'and',
        quickFilterValues: [],
        quickFilterLogicOperator: 'and'
      }
    },
    sorting: {
      sortModel: []
    },
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 50
      }
    }
  };
};

const _defaultUserPreferences: UserPreferences = {
  content: {
    notifications: [],
    historic: [
      {
        id: 'disconnects',
        index: 0,
        value: '12',
        enabled: true
      },
      {
        id: 'cancellations',
        index: 1,
        value: 'None',
        enabled: true
      }
    ],
    order_grid: {
      muiConfig: gridInitialState(WorkflowBaseColumns) as GridInitialStatePro,
      disabledFields: []
    },
    request_grid: {
      muiConfig: gridInitialState(CommonRequestColumns) as GridInitialStatePro,
      disabledFields: []
    },
    user_grid: {
      muiConfig: gridInitialState() as GridInitialStatePro,
      disabledFields: []
    },
    team_grid: {
      muiConfig: gridInitialState() as GridInitialStatePro,
      disabledFields: []
    },
    account_grid: {
      muiConfig: gridInitialState() as GridInitialStatePro,
      disabledFields: []
    },
    case_grid: {
      muiConfig: gridInitialState(DefaultCaseGridColumnDefs) as GridInitialStatePro,
      disabledFields: []
    },
    inventory_grid_All: {
      muiConfig: gridInitialState(
        DefaultInventoryGridColumnDefs,
        InventoryGridVisibilityDefaults
      ) as GridInitialStatePro,
      disabledFields: ['customerLocationName', 'customerLabelOther', 'demarcationDevice']
    },
    // No columns: a layout naming every column would read as saved and hide the account's default layout.
    project_grid_Active: {
      muiConfig: gridInitialState() as GridInitialStatePro,
      disabledFields: []
    },
    project_details_grid: {
      muiConfig: gridInitialState(getDefaultProjectDetailsGridColumnsDefs()) as GridInitialStatePro,
      disabledFields: []
    },
    phone_number_grid: {
      muiConfig: gridInitialState(DefaultPhoneNumberGridColumnDefs) as GridInitialStatePro,
      disabledFields: []
    },
    line_item_grid: {
      muiConfig: gridInitialState(DefaultLineItemGridColumnDefs) as GridInitialStatePro,
      disabledFields: []
    },
    tag_grid: {
      muiConfig: gridInitialState(DefaultTagGridColumnDefs) as GridInitialStatePro,
      disabledFields: []
    },
    price_benchmark_grid_to_market: {
      muiConfig: {
        ...(gridInitialState(priceBenchmarkToMarketColumns) as GridInitialStatePro),
        sorting: {
          sortModel: [
            {
              field: 'priceToMarket',
              sort: 'asc'
            }
          ]
        }
      },
      disabledFields: []
    },
    price_benchmark_grid_needs_review: {
      muiConfig: gridInitialState(priceBenchmarkToNeedsReviewColumns) as GridInitialStatePro,
      disabledFields: []
    },
    widgets: {
      home: [...HomeWidgetDefs],
      cases: [...CasesWidgetDefs],
      inventory: [...InventoryWidgetDefs],
      projects: [...ProjectWidgetDefs]
    },
    comments: true,
    selectedAccounts: []
  }
};

export const getDefaultPreferences = (): UserPreferences => JSON.parse(JSON.stringify(_defaultUserPreferences));

export type WidgetKey = keyof typeof _defaultUserPreferences.content.widgets;
export type PrefKey = keyof UserPreferencesContent;

export enum UserRole {
  CLIENT = 'CLIENT',
  ADVISOR = 'ADVISOR',
  ADMIN = 'ADMIN',
  UNSUPPORTED = 'UNSUPPORTED'
}

export interface CustomView {
  page: string;
  name: string;
}

const userSubject = new BehaviorSubject<User>(defaultUserData);
const userLoadingSubject = new BehaviorSubject<boolean>(false);
const userSavingSubject = new BehaviorSubject<boolean>(false);
const userPreferencesSubject = new BehaviorSubject<UserPreferences>(getDefaultPreferences());
const customViewSubject = new BehaviorSubject<CustomView[]>([]);
const CUSTOM_VIEW_PREFIXES = ['inventory_grid_', 'project_grid_', 'related_project_assets_grid_', 'order_grid_'];
const customViewsOf = (content: UserPreferencesContent): CustomView[] =>
  CUSTOM_VIEW_PREFIXES.flatMap((prefix) => getTabs(content, prefix, true));
const selectedAccountsSubject = new BehaviorSubject<Account[] | null>([]);
const emitErrorState = (message?: string): void => addError('user', message);
let userCustomizationSocketCreated = false;
let userCustomizationSubscription: ReturnType<typeof userCustomizationChannel> | null = null;

const applyUserPreferences = (data: UserPreferences): void => {
  const defaults = getDefaultPreferences();
  const isUserWithAccountId = getUser()?.account_id && getUser()?.account_id !== '';
  const projectDetailsGrid = isUserWithAccountId
    ? data.content?.project_details_grid || defaults.content.project_details_grid
    : defaults.content.project_details_grid;
  const mergedPreferences = {
    ...defaults,
    id: data.id,
    portal_user_id: data.portal_user_id,
    content: {
      ...defaults.content,
      ...data.content,
      project_details_grid: projectDetailsGrid,
      widgets: {
        ...defaults.content.widgets,
        ...(data.content?.widgets ?? {})
      }
    }
  };
  customViewSubject.next(customViewsOf(mergedPreferences.content));
  userPreferencesSubject.next(mergedPreferences);
};

export const createUserCustomizationSocket = () => {
  if (userCustomizationSocketCreated) return;

  const subscription = userCustomizationChannel({
    receivedHandler: (data: { type: string; user_customization: UserPreferences }) => {
      const snapshot = data.user_customization;
      if (!snapshot) return;
      if (isNoNewerThanStore(snapshot)) return;
      notePreferencesVersion(snapshot);
      applyUserPreferences(withUnansweredKeys(snapshot));
    }
  });

  if (!subscription) return;

  userCustomizationSubscription = subscription;
  userCustomizationSocketCreated = true;
};

export const getUserSubject = (): BehaviorSubject<User> => userSubject;
export const getUserLoadingSubject = (): BehaviorSubject<boolean> => userLoadingSubject;
export const getUserSavingSubject = (): BehaviorSubject<boolean> => userSavingSubject;
export const getUserPreferencesSubject = (): BehaviorSubject<UserPreferences> => userPreferencesSubject;
export const getSelectedAccountsSubject = (): BehaviorSubject<Account[] | null> => selectedAccountsSubject;
export const getCustomViewSubject = (): BehaviorSubject<CustomView[]> => customViewSubject;

export const getUser = (): User => userSubject.value;

export const shouldSeeComments = () => {
  return (
    getUser()?.roles?.some(({ name }) => name && SKIP_ACCOUNT_ROLES.includes(name)) ||
    // Comments default to ON: visible unless an account explicitly opted out (=== false)
    getUserPreferencesSubject().getValue().content.comments !== false
  );
};

export const shouldSeeSupplierContacts = () => {
  return (
    getUser()?.roles?.some(({ name }) => name && SKIP_ACCOUNT_ROLES.includes(name)) ||
    getUserPreferencesSubject().getValue().content.supplier_contact
  );
};

// The Reports page is gated solely by the global reports_page feature flag.
export const isReportsPageEnabled = (flags: FeatureFlags): boolean => flags?.reports_page === true;

// Saving/scheduling related-inventory views is gated solely by the global related_inventory_reports flag.
export const isRelatedInventoryReportsEnabled = (flags: FeatureFlags): boolean =>
  flags?.related_inventory_reports === true;

export const isOrdersScheduledReportsEnabled = (flags: FeatureFlags): boolean =>
  flags?.orders_scheduled_reports === true;

export const isInventoryLocationsViewEnabled = (flags: FeatureFlags): boolean =>
  flags?.inventory_locations_view === true;

// The Projects-page redesign (Project Name column, summary stats, at-risk callout) is gated
// solely by the global projects_updates feature flag.
export const isProjectsUpdatesEnabled = (flags: FeatureFlags): boolean => flags?.projects_updates === true;

export const shouldUseThoughtSpotSSO = (): boolean => {
  const user = getUser();
  return user.roles?.some(({ name }) => name && SKIP_ACCOUNT_ROLES.includes(name));
};

export const getThoughtspotToken = async () => {
  if (getUser()?.roles?.some(({ name }) => name && SKIP_ACCOUNT_ROLES.includes(name))) {
    return;
  }
  const { data, error } = await getThoughtspotTokenApi();
  if (data) return data;
  if (error) {
    emitErrorState(error?.message);
  }
};

export const getPreferencesByKey = (
  param: PrefKey
):
  | Preferences[]
  | GridPreferences
  | Widgets
  | OrderRequestPreferences
  | OrdersViewPreferences
  | undefined
  | boolean
  | Account[] => {
  const settings = userPreferencesSubject.getValue();
  const prefs = settings.content[param];

  if (!prefs && param.includes('inventory_grid')) {
    if (param.includes('inventory_grid')) {
      const newGridPrefs = {
        muiConfig: gridInitialState(getColumnsByProduct(param.replace('inventory_grid_', ''))) as GridInitialStatePro,
        disabledFields: [...settings.content['inventory_grid_All'].disabledFields]
      };
      const updatedSettings = {
        ...settings,
        content: {
          ...settings.content,
          [param]: newGridPrefs
        }
      };
      updateUserSettings(updatedSettings, 'Inventory Grid');
      return newGridPrefs;
    }
  }

  return prefs;
};
export const getCurrentPreferences = (): UserPreferences => userPreferencesSubject.getValue();
export const setPreferences = (prefs: UserPreferences) => userPreferencesSubject.next(prefs);
export const isTeamManager = (user?: User): boolean => !!user?.roles?.some((role) => role.name == Role.TEAM_MANAGER);
export const isAdviseTeamManager = (user?: User): boolean =>
  !!user?.roles?.some((role) => role.name === Role.ADVISE_TEAM_MANAGER);
// Either Team Manager runs the scoped Team Settings area for the users on their own team.
export const managesTeam = (user?: User): boolean => isTeamManager(user) || isAdviseTeamManager(user);
export const isAdmin = (user?: User): boolean => !!user?.roles?.some((role) => role.name == Role.ADMIN);
export const isSupport = (user?: User): boolean => !!user?.roles?.some((role) => role.name == Role.SUPPORT);
export const isExecutiveFull = (user?: User): boolean =>
  !!user?.roles?.some((role) => role.name === Role.EXECUTIVE_FULL);
// Mirrors TagPolicy#add_tag_relation? in upstack_api — a literal role that no internal-staff role implies.
export const isInventoryFull = (user?: User): boolean =>
  !!user?.roles?.some((role) => role.name === Role.INVENTORY_FULL);
// Pushed, not pulled: the cell renders from col-defs this module imports, so it reads an import-free home.
userSubject.subscribe((user) => setCanEditTags(isInventoryFull(user)));
export const isSdm = (user?: User): boolean => !!user?.roles?.some((role) => role.name == Role.SDM);
export const isCsm = (user?: User): boolean => !!user?.roles?.some((role) => role.name == Role.CSM);
export const isInternalStaff = (user?: User): boolean =>
  isAdmin(user) ||
  isSupport(user) ||
  isSdm(user) ||
  isCsm(user) ||
  isExecutiveFull(user) ||
  !!user?.roles?.some((role) => role.name === Role.EXECUTIVE_VIEW);
const hasAnyRole = (user: User | undefined, names: string[]): boolean =>
  !!user?.roles?.some((role) => !!role.name && names.includes(role.name));
// True when the user holds any UPSTACK internal-staff role, as upstack_api's
// `PortalUser#internal?` decides. Distinct from `isInternalStaff`, which omits
// the Advisor role for unrelated feature gating.
export const isInternalUser = (user?: User): boolean => hasAnyRole(user, INTERNAL_ROLES);
export const isAdvisor = (user?: User): user is AuthenticatedAdvisor =>
  (user?.permissions?.map((permission) => permission?.name || permission) || []).includes(Permission.VIEW_ADVISOR);
export const isExecutive = (user?: User): user is AuthenticatedAdmin =>
  (user?.permissions?.map((permission) => permission?.name || permission) || []).includes(Permission.VIEW_EXECUTIVE);
// These roles reach the whole directory, so the API sends them an empty `accounts` book; for anyone else it means none.
export const hasAllAccountViewRole = (user?: User): boolean => hasAnyRole(user, ALL_ACCOUNT_VIEW_ROLES);
export const hasAllAccountWriteRole = (user?: User): boolean => hasAnyRole(user, ALL_ACCOUNT_WRITE_ROLES);
// Mirrors PortalUser#advise_all_accounts? in upstack_api: an Advisor role ties an Advise user to their book.
export const hasAdviseAllAccounts = (user?: User): boolean =>
  hasAnyRole(user, ADVISE_STAFF_ROLES) && !hasAnyRole(user, [Role.ADVISOR]);
export const hasAllAccountScope = (user?: User): boolean => hasAllAccountViewRole(user) || hasAdviseAllAccounts(user);
// Mirrors PortalUser#feature_access?: reach from Advise opens a feature only alongside the role for it.
export const hasFeatureAccess = (
  feature: keyof typeof FEATURE_ROLES,
  user: User | undefined = getUser(),
  write = false
): boolean =>
  !hasAdviseAllAccounts(user) ||
  hasAllAccountViewRole(user) ||
  hasAnyRole(user, FEATURE_ROLES[feature][write ? 'write' : 'read']);
// Mirrors the role checks of CommentsPolicy#create?: comments follow inventory, and a role that reads every
// account must also write to every account.
export const canAddComments = (user: User | undefined = getUser()): boolean =>
  hasFeatureAccess('inventory', user) &&
  (hasAllAccountWriteRole(user) || hasAdviseAllAccounts(user) || !hasAllAccountViewRole(user));
export const hasPermissions = (permissions: string[]): boolean => {
  const userPermissions = getUser()?.permissions?.map((permission) => permission.name) || [];
  return permissions.every((permission) => userPermissions.includes(permission));
};
// Mirrors PolicyGate#denied_capabilities: the api refuses the write, so the portal shows the control read-only.
// One list, for the account GET /api/user resolves, which is the account the api's custom-field policies judge;
// a deny bound to another account in the user's book waits on the per-account follow-up.
export const isCapabilityDenied = (capabilityKey: string, user: User | undefined = getUser()): boolean =>
  !!user?.denied_capabilities?.includes(capabilityKey);
export const isClient = (user?: User): user is AuthenticatedClient => user?.role === UserRole.CLIENT;
export const hasAdviseView = (): boolean =>
  hasPermissions([Permission.ADVISE_VIEW]) || hasPermissions([Permission.ADVISE_FULL]);
export const hasAdviseFull = (): boolean => hasPermissions([Permission.ADVISE_FULL]);

// Needs inventory write access, and staff skip the setting; it is the user's own copy, not an order's account's.
export const isQuoteApprovalEnabled = (
  orderRequestPrefs?: OrderRequestPreferences,
  user: User | undefined = getUser()
): boolean =>
  hasFeatureAccess('inventory', user, true) &&
  (orderRequestPrefs?.quoteReviewApprovalWorkflow === true || isInternalUser(user));

// The schema-driven ("advanced") order form is likewise account-gated, but internal
// roles are never account-bound so the customization can't reach them — default them in.
export const isAdvancedOrderFormEnabled = (
  orderRequestPrefs?: OrderRequestPreferences,
  user: User | undefined = getUser()
): boolean =>
  orderRequestPrefs?.isAdvancedForm === true || userInSkipRoles((user?.roles ?? []).map((role) => role.name));

export const isVisibleWidgetByPermissions = (widgetId: WidgetId, user?: User) => {
  const userPermissions = (user?.permissions || []).map((permission) => permission?.name || permission);
  switch (widgetId) {
    case 'cases':
      return [Permission.VIEW_CASES].filter((p) => !userPermissions.includes(p)).length == 0;
    case 'upcomingExpirations':
      return !['Sephora USA', 'STEVEN MADDEN LTD'].includes(user?.account_name || ''); // TODO: temporary changes
    default:
      return true;
  }
};

export const columnsDefault = (config: GridInitialStatePro) => {
  const defaultCols = getDefaultPreferences().content?.inventory_grid_All?.muiConfig?.columns;
  return JSON.stringify(config?.columns) === JSON.stringify(defaultCols);
};

export const widgetsDefault = (w: Widgets) => {
  const defaultWidgets = getDefaultPreferences().content.widgets;
  return JSON.stringify(w) === JSON.stringify(defaultWidgets);
};

/**
 * Stores one key of the preferences content, or removes it when `value` is undefined. It waits until no other write
 * is unanswered, as each was built without this change, and builds on what they stored. The store shows the change
 * at once, so a write sent before this one is answered carries it too. A refused write takes it back out, while a
 * refused removal stays pending: no answer or broadcast brings the key back until one shows it written, and the next
 * write removes it on the server too. If the writes before it are still unanswered after `PREFERENCES_SETTLE_LIMIT_MS`,
 * nothing is sent and it answers with an error to try again; a removal stopped there stays pending as well.
 */
export const updatePreferencesKey = async (key: string, value: unknown, type?: string) => {
  const deadline = Date.now() + PREFERENCES_SETTLE_LIMIT_MS;
  // Checked again after each wait, as a write can be sent while it waits; from the last check on, nothing yields.
  while (preferencesWritesUnanswered()) {
    if (await preferencesWritesSettledBy(deadline)) continue;
    // A write still unanswered was built without this change, and could still land after it and undo it.
    if (value === undefined) {
      markKeyUnanswered(key, undefined);
      userPreferencesSubject.next(withContentKey(getCurrentPreferences(), key, undefined));
    }
    return { data: null, error: { message: PREFERENCES_STILL_SAVING } };
  }
  const previous = (getCurrentPreferences().content as unknown as Record<string, unknown>)[key];
  const removalWasPending = isRemovalPending(key);
  const preferences = withContentKey(getCurrentPreferences(), key, value);
  userPreferencesSubject.next(preferences);
  markKeyUnanswered(key, value);
  let written: Awaited<ReturnType<typeof updateUserSettings>> | undefined;
  try {
    written = await updateUserSettings(preferences, type);
    return written;
  } finally {
    // A removal not written stays pending, as does an earlier one under a value that was not written either.
    if (!written?.data && (value === undefined || removalWasPending)) markKeyUnanswered(key, undefined);
    else markKeyAnswered(key);
    if (!written?.data && value !== undefined) {
      userPreferencesSubject.next(withContentKey(getCurrentPreferences(), key, previous));
    }
  }
};

// An answer can be built before a key write or removal the store already shows, and would otherwise take it back.
const acceptAnswer = (data: UserPreferences): UserPreferences => {
  notePreferencesVersion(data);
  notePersistedRemovals(data);
  return withUnansweredKeys(data);
};

export const updateUserSettings = (
  preferences: UserPreferences,
  type?: string,
  renamedViews?: Record<string, string>
) =>
  trackPreferencesWrite(async () => {
    const { error, data } = await updateSettingsApi(preferences, type, renamedViews);
    if (error) emitErrorState(error.message);
    if (data && !isOlderThanStore(data)) {
      const shown = acceptAnswer(data);
      customViewSubject.next(customViewsOf(shown.content));
      userPreferencesSubject.next(shown);
    }
    return { data, error };
  });

export const updateTableState = (preferences: UserPreferences, type?: string) =>
  trackPreferencesWrite(async () => {
    const { error, data } = await updateSettingsApi(preferences, type);
    if (error) emitErrorState(error.message);
    if (data && !isOlderThanStore(data)) userPreferencesSubject.next(acceptAnswer(data));
    return { data, error };
  });

export const updateUserData = async (first_name?: string, last_name?: string) => {
  const user = getUser();
  const { data, error } = await updateUsersApi(
    { first_name: first_name || user.first_name, last_name: last_name || user.last_name },
    user.id
  );
  if (data) {
    userSubject.next({
      ...userSubject.value,
      first_name: data.first_name,
      last_name: data.last_name
    } as AuthenticatedUser);
    emitErrorState();
  }
  if (error) {
    emitErrorState(error?.message);
  }
  return { data, error };
};

export const createUserSettings = async (preferences: UserPreferences) => {
  const { error, data } = await createSettingsApi(preferences);
  if (error) emitErrorState(error.message);
  if (data) {
    notePreferencesVersion(data);
    userPreferencesSubject.next(data);
  }
  return { data, error };
};

export const adminUrlForRole = (): string => (managesTeam(getUser()) ? '/team-settings' : '/admin');

// Saved one at a time: a late answer to an earlier selection would otherwise land over a later one.
let accountContextSaved: Promise<unknown> = Promise.resolve();

const saveAccountContext = async (accounts: Account[]) => {
  const currentPreferences = getCurrentPreferences();
  if (currentPreferences.id) {
    currentPreferences.content.selectedAccounts = accounts;
    await updateUserSettings(currentPreferences);
  }
};

export const updateAccountContext = async (accounts: Account[]) => {
  selectedAccountsSubject.next(accounts);
  const saved = accountContextSaved.then(() => saveAccountContext(accounts));
  accountContextSaved = saved.catch(() => undefined);
  await saved;
};

export type LinkedAccountOutcome = 'in-scope' | 'switched' | 'unresolved';

// A link may name an account by either of its ids, as `getUserAccountById` reads them.
const isAccount = (acc: Account, accountId: string) => acc.id === accountId || acc.sfId === accountId;

// A directory-wide role's book is empty by design, so the account a link names is looked up instead, and the
// lookup answers exactly the accounts the user may open.
const findLinkedAccount = async (accountId: string): Promise<Account | undefined> => {
  const fromBook = getUserAccountById(accountId);
  if (fromBook || !hasAllAccountScope(getUser())) return fromBook;

  const { data } = await getAccountById(accountId);
  return data && isAccount(data, accountId) ? data : undefined;
};

export const setAccountByUrl = async (accountId?: string): Promise<LinkedAccountOutcome> => {
  if (!accountId) return 'unresolved';
  if (getAccountScope()?.some((acc) => isAccount(acc, accountId))) return 'in-scope';

  const acct = await findLinkedAccount(accountId);
  if (!acct) return 'unresolved';

  const accounts = userInSkipRoles(getUser().roles.map((r) => r.name))
    ? [acct]
    : [...(getSelectedAccountsSubject().getValue() || []), acct];
  // The selection is set before this returns; waiting for it to be saved as well would only delay the page.
  updateAccountContext(accounts).catch((e) => emitErrorState(e instanceof Error ? e.message : String(e)));
  return 'switched';
};

// A saved selection outlives the access that produced it — an Advisor taken off an account still has it stored as their
// last scope, and every request made under it is rejected, which reads as a portal that will not load. Only a
// directory-wide role's book is uninformative when empty; an account-scoped one with no book has lost everything.
const withoutRevokedAccounts = (selected: Account[] | undefined, user: User): Account[] | undefined => {
  if (!selected || hasAllAccountScope(user)) return selected;
  const accessibleIds = new Set(user.accounts.map((acc) => acc.id));
  return selected.filter((acc) => accessibleIds.has(acc.id));
};

export const getAccountScope = (): Account[] | undefined => {
  return getSelectedAccountsSubject().getValue() || undefined;
};

export const getSelectedAccountIdsForUrl = () => {
  const ids = getSelectedAccountIds() || [];
  if (ids.length < 2) return ids;

  const accounts = getAccounts() || [];
  if (ids.length != accounts.length) return ids;

  const allSelected = accounts.every((acc) => ids.includes(acc.id));

  if (allSelected) return ['All'];

  return ids;
};

export const getSelectedAccountIds = () => getAccountScope()?.map((acc) => acc.id);

// The account to act on when the user hasn't explicitly picked one: their own account, else the single
// account they have selected (internal staff have no own account). Null when zero or many are selected.
export const getEffectiveAccountId = (): string | null => {
  const ownAccountId = getUser().account_id;
  if (ownAccountId) return ownAccountId;
  const selected = getSelectedAccountIds();
  return selected?.length === 1 ? selected[0] : null;
};

export const getAccounts = (): Account[] | undefined => {
  const { accounts } = getUser();
  return accounts;
};

export const getPrimaryAccount = (): Account[] | undefined => {
  const user = getUser();
  return user.account_id && user.account_name ? [{ id: user?.account_id, name: user?.account_name }] : undefined;
};

export const getUserAccountById = (id: string): Account | undefined => {
  const accounts = getAccounts();
  return accounts?.find((acc) => acc.id === id || acc.sfId === id);
};

export const clearUserData = () => {
  userCustomizationSubscription?.unsubscribe?.();
  // The api checks the session only when the socket connects, so an open one stays the previous user's.
  userCustomizationSubscription?.consumer?.disconnect?.();
  userCustomizationSubscription = null;
  userSubject.next(defaultUserData);
  userCustomizationSocketCreated = false;
  resetPreferencesWrites();
};

export const useSharedUser = (): UseSharedUserReturn => {
  const { clearSession } = useSession();
  const [params, setParams] = useSearchParams();
  const userFetch = useRef<Promise<void> | null>(null);

  useEffect(() => {
    const sessionSub = getSessionSubject().subscribe((session) => {
      if (session.authenticated && !getUser()?.authenticated) {
        fetchUser();
      }
    });

    return () => {
      if (sessionSub) sessionSub.unsubscribe();
    };
  }, []);

  const fetchUserPreferences = async () => {
    const { data, error } = await fetchUserPreferencesApi();
    if (data) {
      notePreferencesVersion(data);
      applyUserPreferences(withUnansweredKeys(data));
    }
    if (error) emitErrorState(error?.message);
  };

  // Every useSession mount announces the session on boot; one load at a time keeps two from racing over a link.
  const fetchUser = (): Promise<void> => {
    userFetch.current ??= loadUser().finally(() => {
      userFetch.current = null;
    });
    return userFetch.current;
  };

  const loadUser = async () => {
    userLoadingSubject.next(true);
    let loaded = false;
    try {
      const { data, error } = await fetchUserInformation();
      if (data) {
        const role = UserRole[data?.role?.toUpperCase() as UserRole] || UserRole.UNSUPPORTED;
        setDataDogUser({ ...data, role } as User);
        if (process.env.NODE_ENV !== 'test') initializePendo({ ...data, role } as User);
        userSubject.next({ ...data, role, authenticated: true } as AuthenticatedUser);
        emitErrorState();
        await fetchUserPreferences();
        await setUserAccounts();
        loaded = true;
      }

      if (error) {
        datadogLogs.logger.error('Error when fetching user: ', error);

        emitErrorState(error?.message);
        await clearSession();
      }
    } finally {
      // The routes mount on this flag, and routes mounted before a link's account is selected send the user home.
      userLoadingSubject.next(false);
    }
    if (!loaded) return;

    // First paint needs neither, so they wait until the routes are in.
    await getThoughtspotToken();
    createUserCustomizationSocket();
  };

  const selectSavedAccounts = async () => {
    const user = userSubject.getValue();
    const prefs = userPreferencesSubject.getValue();
    const savedAccounts = prefs?.content.selectedAccounts;
    let defaultAccounts = withoutRevokedAccounts(savedAccounts, user);
    const hadRevokedAccounts = defaultAccounts?.length !== savedAccounts?.length;
    if (user.accounts.length === 1 && defaultAccounts?.length === 0) defaultAccounts = user.accounts;
    if (!defaultAccounts) return;

    const sortedDefaultAccounts = defaultAccounts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (hadRevokedAccounts) await updateAccountContext(sortedDefaultAccounts);
    else selectedAccountsSubject.next(sortedDefaultAccounts);
  };

  const setUserAccounts = async () => {
    // A link's account is applied over the saved selection, as from the bell, so one it cannot resolve leaves it be.
    await selectSavedAccounts();
    const accountId = params.get('accountId');
    if (!accountId) return;

    await setAccountByUrl(accountId);
    params.delete('accountId');
    setParams(params, { replace: true });
  };

  const updateUser = async () => {
    const user = getUser();
    const { data, error } = await updateUsersApi({ tos_read: true }, user.id);
    if (data) {
      userSubject.next({ ...userSubject.value, tos_read: true } as AuthenticatedUser);
      emitErrorState();
    }
    if (error) {
      emitErrorState(error?.message);
    }
  };

  const clear = () => userSubject.next(defaultUserData);

  return { clear, accountScope: getAccountScope(), fetchUser, fetchUserPreferences, updateUser };
};
